import axios from "@src/utils/requests/axiosInstance";
import { isDev } from "@src/utils/env";

const CANCELLATION_WINBACK_PATH = "/api/sparks/cancellation-winback";

const apiStates = ["valid", "expired", "redeemed", "invalid"] as const;

type CancellationWinbackApiState = (typeof apiStates)[number];

export type SparksCancellationWinbackResolution = "eligible" | "expired" | "redeemed" | "invalid";

export type SparksCancellationWinbackResolver = (
  winbackId: string,
  options?: { signal?: AbortSignal },
) => Promise<SparksCancellationWinbackResolution>;

type DevelopmentFixtureResolution = SparksCancellationWinbackResolution | "error";

const developmentFixtures: Record<string, DevelopmentFixtureResolution> = {
  "valid-winback-id": "eligible",
  "expired-winback-id": "expired",
  "used-winback-id": "redeemed",
  "invalid-winback-id": "invalid",
  "error-winback-id": "error",
};

export const getSparksCancellationWinbackDevelopmentFixture = (
  winbackId: string,
  isDevelopment = isDev(),
): DevelopmentFixtureResolution | undefined => (isDevelopment ? developmentFixtures[winbackId] : undefined);

const isCancellationWinbackApiState = (value: unknown): value is CancellationWinbackApiState =>
  typeof value === "string" && apiStates.includes(value as CancellationWinbackApiState);

export const resolveSparksCancellationWinback: SparksCancellationWinbackResolver = async (winbackId, options = {}) => {
  const normalizedWinbackId = winbackId.trim();
  if (!normalizedWinbackId) return "invalid";

  const fixture = getSparksCancellationWinbackDevelopmentFixture(normalizedWinbackId);
  if (fixture === "error") {
    throw new Error("Development cancellation-winback resolver failure");
  }
  if (fixture) return fixture;

  const response = await axios.get<unknown>(`${CANCELLATION_WINBACK_PATH}/${encodeURIComponent(normalizedWinbackId)}`, {
    signal: options.signal,
  });
  const state =
    typeof response.data === "object" && response.data !== null && "state" in response.data
      ? response.data.state
      : undefined;

  if (!isCancellationWinbackApiState(state)) {
    throw new Error("Unexpected Sparks cancellation-winback response");
  }

  return state === "valid" ? "eligible" : state;
};
